/**
 * Hook useNotifications — cloche du header (panneau dropdown).
 *
 * 📜 Doctrine pricing (27/03/2026) — 3 niveaux :
 *    ANONYMOUS / FREE / SOLO. Le panneau ne parle QUE des conversations IA
 *    (les sondages sont illimités partout, donc rien à notifier à leur sujet).
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuota } from "./useQuota";
import {
  creditCalculationService,
  CreditInfo,
  CreditPlanType,
} from "@core/services/CreditCalculationService";
import {
  NotificationState,
  NotificationItem,
  NotificationConfig,
  NotificationBadgeState,
  NotificationBadgeColor,
} from "@core/types/notifications";
import { PLANS } from "@core/constants/quotas";

const STORAGE_KEYS = {
  state: "doodates_notifications_state",
  dismissed: "doodates_notifications_dismissed",
  lastCheck: "doodates_notifications_last_check",
} as const;

const DEFAULT_CONFIG: NotificationConfig = {
  enableAutoShow: true,
  maxNotificationsPerSession: 5,
  cooldownPeriod: 24, // heures
  persistentNotifications: true,
};

export function useNotifications(config: Partial<NotificationConfig> = {}) {
  const { t, i18n } = useTranslation();
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const {
    status: quotaStatus,
    isAuthenticated,
    limits,
    isTrialActive,
  } = useQuota();

  // État local
  const [state, setState] = useState<NotificationState>(() =>
    loadNotificationState(),
  );
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [badgeState, setBadgeState] = useState<NotificationBadgeState>(() => ({
    visible: false,
    color: "gray",
    count: 0,
    pulse: false,
  }));

  // Déduire le plan réel (ANONYMOUS / FREE / SOLO) depuis useQuota.
  // SOLO est détecté par sa limite de conversations IA "unlimited".
  const planType: CreditPlanType = useMemo(() => {
    if (!isAuthenticated) return "ANONYMOUS";
    const soloLimit = PLANS.SOLO.features.aiConversations;
    const isSoloUnlimited = soloLimit === "unlimited";
    // Si la limite affichée correspond à une limite illimitée (99999+), c'est SOLO.
    if (isSoloUnlimited && limits.conversations >= 99999) return "SOLO";
    return "FREE";
  }, [isAuthenticated, limits.conversations]);

  // Calcul des informations de conversations IA
  // PENDANT UN ESSAI, LE PLAN NE SUFFIT PAS À DÉDUIRE LE PLAFOND.
  //
  // `planType` ci-dessus repose sur un seuil : `limits.conversations >= 99999`
  // signifie SOLO, sinon FREE. Or pendant l'essai `useQuota` renvoie
  // TRIAL_AI_CONVERSATIONS (15) — ni 3 ni 99999 — donc la cloche concluait
  // FREE et calculait contre 3. Un essayeur voyait « Limite atteinte — 3/3 »
  // à sa troisième conversation, pendant que le badge du tableau de bord
  // affichait correctement 3/15. Il lui en restait douze.
  //
  // C'est la même cause que le défaut trouvé au test local du 18/09
  // (« Conversations illimitées » affiché à quelqu'un qui en a 15) : un
  // SECOND lecteur du même état, qu'on a oublié de mettre à jour quand le
  // carve-out a été posé dans useQuota. On lui passe donc le plafond réel au
  // lieu de le lui faire deviner.
  //
  // Les messages n'ont pas besoin d'être réécrits : ils sont tous
  // paramétrés par used/limit/remaining, donc « Limite atteinte — 15/15 » et
  // « Presque à votre limite — 12/15 » tombent juste d'eux-mêmes.
  const creditInfo = useMemo<CreditInfo>(
    () =>
      creditCalculationService.calculateCredits(
        quotaStatus.conversations.used,
        planType,
        isTrialActive ? { limitOverride: limits.conversations } : {},
      ),
    [
      quotaStatus.conversations.used,
      planType,
      isTrialActive,
      limits.conversations,
    ],
  );

  // Détermine la couleur du badge selon la priorité
  const getBadgeColor = useCallback((): NotificationBadgeColor => {
    if (creditInfo.planType === "SOLO") return "gray";
    if (creditInfo.isAtLimit) return "red";
    if (creditInfo.isNearLimit) return "orange";
    if (Number.isFinite(creditInfo.remaining) && creditInfo.remaining <= 3)
      return "blue";
    return "gray";
  }, [creditInfo]);

  // Génère les notifications de quota IA
  // IDs STABLES (sans timestamp) pour permettre la persistance "dismiss"
  // dans state.dismissedNotifications — sinon chaque rerender crée un nouvel
  // ID qui contourne le filtre dismissed et ressuscite la notif.
  const generateQuotaNotifications = useCallback((): NotificationItem[] => {
    const items: NotificationItem[] = [];
    const now = new Date();
    const dismissed = state.dismissedNotifications;

    // SOLO : pas de notification — IA illimitée
    if (creditInfo.planType === "SOLO") return items;

    // ANONYMOUS : invitation à s'inscrire — notification distincte et
    // prioritaire à l'épuisement des 3 conversations à vie (id différent de
    // "quota-anonymous" pour ne pas hériter d'un dismiss antérieur, et pour
    // que le badge reste rouge/pulse tant qu'elle n'est pas vue).
    if (creditInfo.planType === "ANONYMOUS") {
      const id = creditInfo.isAtLimit
        ? "quota-anonymous-limit"
        : "quota-anonymous";
      if (!dismissed.has(id)) {
        items.push({
          id,
          type: "quota",
          priority: creditInfo.isAtLimit ? "critique" : "moyenne",
          title: t(
            creditInfo.isAtLimit
              ? "notifications.quota_anonymous_limit_title"
              : "notifications.quota_anonymous_title",
          ),
          message:
            creditCalculationService.generateNotificationMessage(creditInfo),
          callToAction: {
            label: t("notifications.create_account"),
            url: "/auth",
          },
          timestamp: now,
          read: false,
          dismissible: true,
        });
      }
      return items;
    }

    // FREE
    if (creditInfo.isAtLimit) {
      const id = "quota-limit";
      if (!dismissed.has(id)) {
        items.push({
          id,
          type: "quota",
          priority: "critique",
          title: t("notifications.quota_limit_title"),
          message:
            creditCalculationService.generateNotificationMessage(creditInfo),
          callToAction: {
            label: t("notifications.upgrade_solo"),
            url: "/pricing",
          },
          secondaryCallToAction: {
            label: t("notifications.view_usage"),
            url: "/journal",
          },
          timestamp: now,
          read: false,
          dismissible: true,
        });
      }
    } else if (creditInfo.isNearLimit) {
      const id = "quota-near";
      if (!dismissed.has(id)) {
        items.push({
          id,
          type: "quota",
          priority: "élevée",
          title: t("notifications.quota_near_title"),
          message:
            creditCalculationService.generateNotificationMessage(creditInfo),
          callToAction: {
            label: t("notifications.upgrade_solo"),
            url: "/pricing",
          },
          secondaryCallToAction: {
            label: t("notifications.view_usage"),
            url: "/journal",
          },
          timestamp: now,
          read: false,
          dismissible: true,
        });
      }
    } else if (
      Number.isFinite(creditInfo.remaining) &&
      creditInfo.remaining <= 3
    ) {
      const id = "quota-low";
      if (!dismissed.has(id)) {
        items.push({
          id,
          type: "quota",
          priority: "moyenne",
          title: t("notifications.quota_low_title"),
          message:
            creditCalculationService.generateNotificationMessage(creditInfo),
          callToAction: {
            label: t("notifications.upgrade_solo"),
            url: "/pricing",
          },
          timestamp: now,
          read: false,
          dismissible: true,
        });
      }
    }

    return items;
  }, [creditInfo, state.dismissedNotifications, t]);

  // Met à jour le badge à partir d'une liste de notifications donnée
  // (évite de lire un état `notifications` périmé via closure).
  const updateBadge = useCallback(
    (items: NotificationItem[] = notifications) => {
      const unreadCount = items.filter((n) => !n.read).length;
      // Gate color on unreadCount > 0. getBadgeColor() reads creditInfo and
      // returns "red" when isAtLimit, regardless of whether anything is
      // visible — that bled into NotificationBadge's iconColors[color] and
      // made the bell glyph itself red even after every notif was dismissed.
      const color = unreadCount > 0 ? getBadgeColor() : "gray";
      const shouldShow =
        unreadCount > 0 &&
        creditCalculationService.shouldShowNotification(
          creditInfo,
          state.lastShown,
        );

      setBadgeState({
        visible: shouldShow || unreadCount > 0,
        color,
        count: unreadCount,
        // Only pulse when there are actual unread notifications. Without this
        // gate, the bell keeps pulsing red after the user dismissed every
        // notif because creditInfo.isAtLimit/isNearLimit remain true.
        pulse:
          unreadCount > 0 && (creditInfo.isAtLimit || creditInfo.isNearLimit),
      });
    },
    [creditInfo, notifications, state.lastShown, getBadgeColor],
  );

  // Ouvre/Ferme le panneau
  const togglePanel = useCallback(() => {
    setState((prev) => {
      const newState = { ...prev, isOpen: !prev.isOpen };

      // Marquer comme lues si ouverture
      if (!prev.isOpen && newState.isOpen) {
        newState.lastShown = new Date();
        setNotifications((prevNotifications) =>
          prevNotifications.map((n) => ({ ...n, read: true })),
        );
      }

      saveNotificationState(newState);
      return newState;
    });
  }, []);

  // Ferme le panneau et marque les notifications affichées comme "vues"
  // (ajoutées à dismissedNotifications) pour qu'elles ne reviennent pas dès le
  // prochain re-render. Sans ça, generateQuotaNotifications les régénère avec
  // read=false à chaque changement de creditInfo et la cloche reste allumée.
  //
  // Functional updates pour `state` ET `notifications` : sinon une closure
  // périmée écrase dismissedNotifications avec un Set vide après un dismiss
  // récent, et la notif fermée ressuscite à la prochaine régénération.
  const closePanel = useCallback(() => {
    setNotifications((prevNotifications) => {
      const visibleIds = prevNotifications.map((n) => n.id);
      setState((prev) => {
        if (!prev.isOpen) return prev;
        const newDismissed = new Set(prev.dismissedNotifications);
        visibleIds.forEach((id) => newDismissed.add(id));
        const newState = {
          ...prev,
          isOpen: false,
          lastShown: new Date(),
          dismissedNotifications: newDismissed,
        };
        saveNotificationState(newState);
        return newState;
      });
      return [];
    });
  }, []);

  // Ignore une notification
  const dismissNotification = useCallback((notificationId: string) => {
    setState((prev) => {
      const newDismissed = new Set(prev.dismissedNotifications);
      newDismissed.add(notificationId);

      const newState = {
        ...prev,
        dismissedNotifications: newDismissed,
      };

      saveNotificationState(newState);
      return newState;
    });

    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  }, []);

  // Ajoute une notification manuellement
  const addNotification = useCallback(
    (notification: Omit<NotificationItem, "id" | "timestamp">) => {
      const newNotification: NotificationItem = {
        ...notification,
        id: `manual-${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
      };

      setNotifications((prev) => {
        const updated = [newNotification, ...prev];
        return updated.slice(0, 50);
      });
    },
    [],
  );

  // Efface toutes les notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setState((prev) => {
      const newState = { ...prev, unreadCount: 0 };
      saveNotificationState(newState);
      return newState;
    });
  }, []);

  // Effet principal : mise à jour des notifications et du badge
  useEffect(() => {
    const newNotifications = generateQuotaNotifications();
    setNotifications(newNotifications);
    updateBadge(newNotifications);
    // updateBadge dépend de notifications, mais on ne veut pas boucler :
    // on se repose sur creditInfo comme clé de dépendance.
    // dismissedNotifications.size couvre les dismiss runtime sans tracker la
    // référence du Set (qui change à chaque setState).
  }, [
    creditInfo.used,
    creditInfo.limit,
    creditInfo.remaining,
    creditInfo.isNearLimit,
    creditInfo.isAtLimit,
    creditInfo.planType,
    state.dismissedNotifications.size,
    // Régénère les notifications (titres + corps) au changement de langue.
    i18n.language,
  ]);

  // Persistance
  useEffect(() => {
    if (finalConfig.persistentNotifications) {
      saveNotificationState(state);
    }
  }, [state, finalConfig.persistentNotifications]);

  return {
    // État
    isOpen: state.isOpen,
    notifications,
    badgeState,
    creditInfo,

    // Actions
    togglePanel,
    closePanel,
    dismissNotification,
    addNotification,
    clearNotifications,

    // Utilitaires
    hasUnreadNotifications: notifications.some((n) => !n.read),
    unreadCount: notifications.filter((n) => !n.read).length,
    encouragementMessage:
      creditCalculationService.generateEncouragementMessage(creditInfo),
  };
}

// ============================================================================
// Persistance locale
// ============================================================================

function loadNotificationState(): NotificationState {
  try {
    const dismissed = localStorage.getItem(STORAGE_KEYS.dismissed);
    return {
      isOpen: false,
      unreadCount: 0,
      lastShown: null,
      dismissedNotifications: dismissed
        ? new Set(JSON.parse(dismissed))
        : new Set(),
      language: "fr",
    };
  } catch {
    return {
      isOpen: false,
      unreadCount: 0,
      lastShown: null,
      dismissedNotifications: new Set(),
      language: "fr",
    };
  }
}

function saveNotificationState(state: NotificationState): void {
  try {
    localStorage.setItem(
      STORAGE_KEYS.state,
      JSON.stringify({
        isOpen: state.isOpen,
        unreadCount: state.unreadCount,
        lastShown: state.lastShown?.toISOString() || null,
      }),
    );

    localStorage.setItem(
      STORAGE_KEYS.dismissed,
      JSON.stringify(Array.from(state.dismissedNotifications)),
    );

    localStorage.setItem(STORAGE_KEYS.lastCheck, new Date().toISOString());
  } catch {
    // Ignorer erreurs localStorage
  }
}
